import React, { useCallback, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { AxiosError } from 'axios';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { RouteNavigationParams } from 'router/types';

import { selectLayoutConfig } from 'store/config/selectors';
import { hidePlayer } from 'store/player/actions';
import { selectPlayerConfig } from 'store/player/selectors';
import {
    loginSilently,
    logout,
    setRefreshTokenError,
} from 'store/user/actions';
import {
    selectRefreshTokenError,
    selectUserAuthData,
} from 'store/user/selectors';
import { isVoluntarySuspension } from 'store/user/utils';

import useNoInternet from 'utils/hooks/useNoInternet';
import { androidScreenMarginBottom } from 'utils/responsiveness';

import { onAfterHidePlayerModal } from 'screens/PlayerModal/utils';

import DialogView from 'components/Dialog';

const TokenRefreshFailureModal = () => {
    const dispatch = useDispatch();

    const refreshTokenError = useSelector(selectRefreshTokenError);
    const authData = useSelector(selectUserAuthData);
    const { visible: playerVisible } = useSelector(selectPlayerConfig);
    const { primary } = useSelector(selectLayoutConfig);

    const interval = useRef<undefined | NodeJS.Timeout>(undefined);

    const navigation =
        useNavigation<StackNavigationProp<RouteNavigationParams>>();

    const { isInternetConnected } = useNoInternet();
    const { t } = useTranslation();

    const handleLogout = useCallback(() => {
        dispatch(logout(navigation));
    }, [dispatch, navigation]);

    const handleActionButtonPress = useCallback(() => {
        if (isInternetConnected) {
            dispatch(setRefreshTokenError(null));
        }
    }, [dispatch, isInternetConnected]);

    useEffect(() => {
        if (authData) {
            if (interval.current && !isInternetConnected) {
                clearInterval(interval.current);
                interval.current = undefined;
            }

            if (interval.current) {
                clearInterval(interval.current);
                interval.current = undefined;
            }

            if (isInternetConnected) {
                interval.current = setInterval(() => {
                    if (
                        authData.creation_time +
                            (authData.expires_in - 10) * 1000 <
                        Date.now()
                    ) {
                        // the token expired
                        dispatch(
                            loginSilently({
                                onFailure(error) {
                                    if (!isVoluntarySuspension(error)) {
                                        dispatch(
                                            setRefreshTokenError(
                                                error as AxiosError,
                                            ),
                                        );
                                    }
                                },
                            }),
                        );
                    }
                }, 5000);
            }
        }

        return () => {
            if (interval.current) {
                clearInterval(interval.current);
                interval.current = undefined;
            }
        };
    }, [authData, isInternetConnected]);

    useEffect(() => {
        if (refreshTokenError) {
            if (isInternetConnected) {
                handleLogout();
            }

            if (playerVisible) {
                onAfterHidePlayerModal();
                dispatch(hidePlayer());
            }
        }
    }, [refreshTokenError, handleLogout, dispatch, isInternetConnected]);

    return (
        <DialogView
            visible={!!refreshTokenError}
            title={LANGUAGE_KEYS.SCREEN_ALERT_TOKEN_EXPIRED_TITLE}
            description={LANGUAGE_KEYS.SCREEN_ALERT_TOKEN_EXPIRED_SUBTITLE}
            actionButtonTitle={LANGUAGE_KEYS.OK_BUTTON}
            actionButtonAccessibilityLabel={t(LANGUAGE_KEYS.OK_BUTTON)}
            actionButtonFillColor={primary}
            actionButtonOnPress={handleActionButtonPress}
            style={{
                marginBottom: androidScreenMarginBottom,
            }}
        />
    );
};

export default TokenRefreshFailureModal;
